import { useState } from 'react';
import { IoMdClose } from 'react-icons/io';
import { FiSave } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { collateralApi } from '../../../services/collateralApi';
import ConfigureCriteriaForm from './ConfigureCriteriaForm';

const CRITERIA_KEY_MAP: Record<string, string> = {
    validation_xml: 'XML', validation_invoice_pdf: 'INVOICE_PDF',
    validation_receipt_stub: 'RECEIPT_STUB', validation_contact: 'DRAWEE_CONFIRMATION',
    validation_sefaz: 'SEFAZ', validation_accepted_bill: 'ACCEPTED_BILL',
    validation_purchase_contract: 'PURCHASE_CONTRACT',
    validation_debtor_notification: 'DEBTOR_NOTIFICATION',
};

interface Props {
    configUuid: string;
    validationConfig: Record<string, string>;
    operationSummary: { operationCode: string; assignorName: string; totalAmount: number; totalReceivables: number };
    operationType: string;
    obligationType: string;
    traderName: string;
    analysisCriteria: 'Completa' | 'Amostragem';
    sampledTitles: number;
    hasDraweeInProgress: boolean;
    onClose: () => void;
    onSaved: () => void;
}

const CriteriaConfigPanelEdit = ({
    configUuid, validationConfig: initialConfig, operationSummary,
    operationType: initialOpType, obligationType: initialObType, traderName: initialTrader,
    analysisCriteria: initialCriteria, sampledTitles: initialSampled,
    hasDraweeInProgress, onClose, onSaved,
}: Props) => {
    const [operationType, setOperationType] = useState(initialOpType);
    const [obligationType, setObligationType] = useState(initialObType);
    const [traderName, setTraderName] = useState(initialTrader);
    const [validationConfig, setValidationConfig] = useState<Record<string, string>>(() => {
        const initial: Record<string, string> = {
            validation_xml: 'DO_NOT_VALIDATE', validation_invoice_pdf: 'DO_NOT_VALIDATE',
            validation_receipt_stub: 'DO_NOT_VALIDATE', validation_accepted_bill: 'DO_NOT_VALIDATE',
            validation_contact: 'DO_NOT_VALIDATE', validation_purchase_contract: 'DO_NOT_VALIDATE',
            validation_sefaz: 'DO_NOT_VALIDATE', validation_debtor_notification: 'DO_NOT_VALIDATE',
        };
        Object.entries(initialConfig).forEach(([k, v]) => { if (v) initial[k] = v; });
        return initial;
    });
    const [analysisCriteria, setAnalysisCriteria] = useState<'Completa' | 'Amostragem'>(initialCriteria);
    const [sampledTitles, setSampledTitles] = useState(initialSampled || 1);
    const [isSaving, setIsSaving] = useState(false);

    const handleSave = async () => {
        const criteria: { criteria_type: string; timing: string }[] = [];
        Object.entries(validationConfig).forEach(([key, val]) => {
            const type = CRITERIA_KEY_MAP[key];
            if (type && val && val !== 'DO_NOT_VALIDATE') {
                criteria.push({ criteria_type: type, timing: val });
            }
        });
        if (criteria.length === 0) {
            toast.warn('Selecione ao menos um critério de validação.');
            return;
        }
        setIsSaving(true);
        try {
            const result = await collateralApi.editVerificationCriteria(configUuid, criteria, {
                operationType, obligationType, traderName,
                analysisMode: analysisCriteria === 'Amostragem' ? 'SAMPLE' : 'COMPLETE',
                sampledTitles: analysisCriteria === 'Amostragem' ? sampledTitles : undefined,
            });
            if (result.rejectedRemovals?.length > 0) {
                const rejected = result.rejectedRemovals.map((r: any) => r.criteriaType).join(', ');
                toast.warn(`Não foi possível remover: ${rejected} (já possui validações em andamento)`);
            } else {
                toast.success('Critérios de validação alterados com sucesso');
            }
            onSaved();
        } catch {
            toast.error('Erro ao alterar critérios de validação');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex">
            <div className="flex-1 bg-black/30" onClick={onClose} />
            <button onClick={onClose}
                className="fixed top-[15px] right-[585px] w-8 h-8 rounded-full shadow-lg z-[9999] flex items-center justify-center border border-gray-300 dark:border-slate-600 bg-gray-500 hover:bg-gray-600 text-white">
                <IoMdClose size={16} />
            </button>
            <div className="w-[600px] h-full bg-white dark:bg-slate-800 shadow-xl overflow-y-auto border-l border-gray-200 dark:border-slate-700">
                <div className="sticky top-0 flex items-center justify-between p-4 border-b border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 z-10">
                    <span className="text-sm font-semibold dark:text-white">Alterar Critérios de Validação</span>
                    <button onClick={handleSave} disabled={isSaving}
                        className="px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 text-xs flex items-center space-x-1 disabled:opacity-50">
                        <FiSave size={12} /> <span>{isSaving ? 'Salvando...' : 'Salvar'}</span>
                    </button>
                </div>
                <div className="p-4">
                    <ConfigureCriteriaForm
                        operationSummary={operationSummary}
                        operationType={operationType} setOperationType={setOperationType}
                        obligationType={obligationType} setObligationType={setObligationType}
                        traderName={traderName} setTraderName={setTraderName}
                        validationConfig={validationConfig} setValidationConfig={setValidationConfig}
                        analysisCriteria={analysisCriteria} setAnalysisCriteria={setAnalysisCriteria}
                        sampledTitles={sampledTitles} setSampledTitles={setSampledTitles}
                        disabled={isSaving}
                        draweeCannotBeDisabled={hasDraweeInProgress}
                    />
                </div>
            </div>
        </div>
    );
};

export default CriteriaConfigPanelEdit;
